import { useState, useEffect, useCallback } from 'react';
import { 
  getScannerSession, 
  clearScannerSession, 
  extendScannerSession,
  isValidScannerSession,
  type ScannerSession 
} from '@/lib/attendance';

interface UseScannerAuthReturn {
  /** Current scanner session if authenticated */
  session: ScannerSession | null;
  /** Whether the scanner is currently authenticated */
  isAuthenticated: boolean;
  /** Whether the authentication state is being checked */
  loading: boolean;
  /** Program ID from the current session */
  programId: string | null;
  /** Business ID from the current session */
  businessId: string | null;
  /** Program name from the current session */
  programName: string | null;
  /** Program type from the current session */
  programType: string | null;
  /** Username from the current session */
  username: string | null;
  /** Logout from scanner session */
  logout: () => void;
  /** Refresh/extend current session */
  refreshSession: () => boolean;
  /** Check if session is still valid and refresh state */
  checkSession: () => void;
}

/**
 * Hook for managing scanner authentication state
 * Provides session management and authentication status for scanner users
 */
export const useScannerAuth = (): UseScannerAuthReturn => {
  const [session, setSession] = useState<ScannerSession | null>(null);
  const [loading, setLoading] = useState(true);

  // Check session validity and update state
  const checkSession = useCallback(() => {
    setLoading(true);
    try {
      const currentSession = getScannerSession();
      setSession(currentSession);
    } catch (error) {
      console.error('Error checking scanner session:', error);
      setSession(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Logout and clear session
  const logout = useCallback(() => {
    clearScannerSession();
    setSession(null);
  }, []);

  // Refresh/extend current session
  const refreshSession = useCallback((): boolean => {
    if (!session) return false;
    
    const success = extendScannerSession();
    if (success) {
      // Update local session state with new expiry
      const updatedSession = getScannerSession();
      setSession(updatedSession);
    }
    return success;
  }, [session]);

  // Initial session check
  useEffect(() => {
    checkSession();
  }, [checkSession]);

  // Set up session monitoring
  useEffect(() => {
    if (!session) return;

    // Check session validity every 5 minutes
    const interval = setInterval(() => {
      if (!isValidScannerSession()) {
        setSession(null);
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [session]);

  // Auto-refresh session before expiry
  useEffect(() => {
    if (!session) return;

    const timeUntilExpiry = session.expires_at - Date.now();
    const refreshTime = Math.max(timeUntilExpiry - (30 * 60 * 1000), 1000); // 30 minutes before expiry

    if (refreshTime > 0) {
      const timeout = setTimeout(() => {
        refreshSession();
      }, refreshTime);

      return () => clearTimeout(timeout);
    }
  }, [session, refreshSession]);

  // Listen for storage changes (multi-tab support)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'taginn_scanner_session') {
        checkSession();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [checkSession]);

  return {
    session,
    isAuthenticated: session !== null,
    loading,
    programId: session?.program_id || null,
    businessId: session?.business_id || null,
    programName: session?.program_name || null,
    programType: session?.program_type || null,
    username: session?.username || null,
    logout,
    refreshSession,
    checkSession
  };
};

export default useScannerAuth;